/**
 * The conversation about one node, in a panel at the right of the workspace (under the account
 * menu): which node it is about, shown at its top with a way to bring the node into view, the
 * comments oldest first with who wrote each and when, and a field to write one. Everyone the
 * workspace is shared with sees the same comments, as they are written (utils/commentStore.ts).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, LocateFixed, MessageSquare, SendHorizontal, Trash2, X } from 'lucide-react';
import type { NodeType } from '../types';
import { NodeKindIcon, nodeTone } from './nodes/nodeTheme';
import { Avatar } from './ideas/parts';
import { agoLabel } from '../utils/ideas';
import { MAX_COMMENT_CHARS, mayDeleteComment, type NodeComment } from '../utils/nodeComments';

interface Props {
    /** The node the comments are about; null when it has been deleted since */
    node: { id: string; type: NodeType; title: string } | null;
    /** Its comments, oldest first */
    comments: NodeComment[];
    userId?: string;
    isOwner: boolean;
    /** Comments can be written here: a cloud workspace, someone signed in */
    canWrite: boolean;
    /** Why comments cannot be read or written, when they cannot */
    problem: string | null;
    loading: boolean;
    onSend: (text: string) => Promise<void>;
    onDelete: (commentId: string) => Promise<void>;
    onShowNode: () => void;
    onClose: () => void;
}

export const NodeCommentsPanel: React.FC<Props> = ({ node, comments, userId, isOwner, canWrite, problem, loading, onSend, onDelete, onShowNode, onClose }) => {
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLTextAreaElement>(null);

    // Another node's comments: a fresh field, ready to type in
    useEffect(() => { setText(''); setError(null); fieldRef.current?.focus(); }, [node?.id]);
    // The newest at the bottom stays in view as comments come in
    useEffect(() => {
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [comments.length, node?.id]);

    const send = async () => {
        if (sending || !text.trim() || !node) return;
        const typed = text;
        setSending(true);
        setError(null);
        setText('');
        try {
            await onSend(typed);
        } catch (e) {
            // What was typed comes back, so nothing is lost
            setText(typed);
            setError(e instanceof Error && e.message ? e.message : 'The comment could not be sent.');
        } finally {
            setSending(false);
            fieldRef.current?.focus();
        }
    };

    const remove = async (commentId: string) => {
        setError(null);
        try {
            await onDelete(commentId);
        } catch (e) {
            setError(e instanceof Error && e.message ? e.message : 'The comment could not be deleted.');
        }
    };

    return (
        <aside
            className="ns-drawer absolute right-0 top-[72px] h-[calc(100%-72px)] w-[360px] max-w-[90vw] z-40 overflow-hidden flex flex-col animate-[ns-slide-up_240ms_cubic-bezier(0.34,1.45,0.5,1)]"
            data-canvas-drawer="comments"
            aria-label="Comments"
        >
            <div className="ns-drawer-head">
                <h2 className="ns-t-heading flex items-center gap-2">
                    <MessageSquare size={16} className="text-ink-soft" /> Comments
                </h2>
                <button type="button" onClick={onClose} className="ns-icon-btn" title="Close" aria-label="Close comments">
                    <X />
                </button>
            </div>

            {/* Which node this conversation is about */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-cream-300" data-comments-node={node?.id ?? ''}>
                {node ? (
                    <>
                        <span data-tone={nodeTone(node.type)} className="grid place-items-center w-9 h-9 shrink-0 rounded-full bg-[color:var(--tone-soft)] text-[color:var(--tone-deep)]" aria-hidden="true">
                            <NodeKindIcon type={node.type} size={16} />
                        </span>
                        <div className="min-w-0 flex-1">
                            <p className="ns-t-label">About the node</p>
                            <p className="text-sm font-semibold text-ink truncate" title={node.title}>{node.title}</p>
                        </div>
                        <button type="button" onClick={onShowNode} className="ns-btn ns-btn-xs ns-btn-ghost ns-btn-flat shrink-0" title="Bring this node into view and select it">
                            <LocateFixed size={14} aria-hidden="true" /> Show
                        </button>
                    </>
                ) : (
                    <p className="text-sm text-ink-soft">This node has been deleted. Its comments are kept.</p>
                )}
            </div>

            {/* The conversation */}
            <div ref={listRef} className="flex-1 min-h-0 overflow-auto px-4 py-3 space-y-4" data-comment-list>
                {loading ? (
                    <p className="flex items-center gap-2 text-sm text-ink-soft" role="status"><Loader2 size={16} className="animate-spin" aria-hidden="true" />Reading the comments</p>
                ) : comments.length === 0 ? (
                    <p className="text-sm text-ink-soft">No comments on this node yet. Feedback and questions written here stay with it, for everyone the workspace is shared with.</p>
                ) : comments.map(comment => {
                    const mine = comment.authorId === userId;
                    return (
                        <div key={comment.id} className="group flex items-start gap-2.5" data-comment={comment.id}>
                            <Avatar member={{ name: comment.authorName, picture: comment.authorPicture }} size={32} />
                            <div className="min-w-0 flex-1">
                                <p className="flex items-baseline gap-2 text-xs">
                                    <span className="font-semibold text-ink truncate">{mine ? 'You' : comment.authorName}</span>
                                    <span className="ns-t-meta shrink-0" title={new Date(comment.createdAt).toLocaleString()}>{agoLabel(comment.createdAt)}</span>
                                </p>
                                <p className="mt-0.5 text-sm text-ink whitespace-pre-wrap [overflow-wrap:anywhere]">{comment.text}</p>
                            </div>
                            {mayDeleteComment(comment, userId, isOwner) && (
                                <button type="button" onClick={() => remove(comment.id)} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100" aria-label="Delete this comment" title="Delete this comment">
                                    <Trash2 />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Writing one */}
            <div className="border-t border-cream-300 p-3 space-y-2">
                {(error || problem) && <p className="ns-note" data-tone="warn" role="alert"><span>{error || problem}</span></p>}
                {canWrite && node ? (
                    <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); void send(); }}>
                        <textarea
                            ref={fieldRef}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => {
                                // Enter sends; Shift+Enter starts a new line
                                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(); }
                            }}
                            rows={2}
                            maxLength={MAX_COMMENT_CHARS}
                            placeholder={`Comment on ${node.title}`}
                            aria-label="Write a comment"
                            className="ns-field flex-1 min-h-[44px] max-h-40 resize-none text-sm"
                        />
                        <button type="submit" disabled={sending || !text.trim()} className="ns-icon-btn ns-icon-btn-solid shrink-0" aria-label="Send comment" title="Send (Enter)">
                            {sending ? <Loader2 className="animate-spin" /> : <SendHorizontal />}
                        </button>
                    </form>
                ) : !canWrite ? (
                    <p className="text-sm text-ink-soft">Comments need a cloud workspace and a signed-in account.</p>
                ) : null}
            </div>
        </aside>
    );
};

export default NodeCommentsPanel;
